// Nội dung màn Hướng dẫn sử dụng Block trang chủ (HomeBlockGuidePage.tsx).
// Ảnh chụp nằm ở public/guide/home-block/<file>.webp; số khoanh đỏ trên ảnh ứng với thứ tự trong marks.
import type { HomeBlockTemplate } from "./homeBlockData";

export type GuideShot = {
  file: string;
  caption: string;
  marks: string[];
};

export type GuideSection = {
  title: string;
  intro?: string;
  steps?: string[];
  shot?: GuideShot;
  tips?: string[];
};

export type GuideTopic = {
  key: string;
  title: string;
  summary: string;
  sections: GuideSection[];
};

export const GUIDE_BASICS: GuideTopic[] = [
  {
    key: "danh-sach",
    title: "Màn danh sách block",
    summary:
      "Nơi sắp xếp thứ tự các block trên trang chủ, bật tắt, chọn thiết bị, đặt lịch hiển thị và mở trình sửa nội dung. Mọi thao tác ở màn này được lưu ngay, không cần bấm Lưu.",
    sections: [
      {
        title: "Các vùng trên màn danh sách",
        shot: {
          file: "list-overview",
          caption: "Màn danh sách block trang chủ",
          marks: [
            "Lọc block: Tất cả, Đang hiển thị, Đang ẩn, Có lịch. Số bên cạnh là số block của từng nhóm.",
            "Tìm block theo tên hoặc mã block.",
            "Mở trang hướng dẫn sử dụng này.",
            "Xem theo thiết bị. Chọn Điện thoại để thấy block nào không hiện trên điện thoại: dòng đó mờ đi và có nhãn cam.",
            "Thêm block mới từ một mẫu có sẵn.",
            "Tay kéo: giữ và kéo lên, xuống để đổi vị trí block trên trang chủ.",
            "Công tắc Bật: bật hoặc tắt block trên trang chủ.",
            "Mũi tên lên, xuống để dịch block từng vị trí. Nút bút chì mở trình sửa nội dung.",
            "Bảng chi tiết của block đang chọn.",
          ],
        },
        tips: [
          "Dòng mờ là block đang tắt, hoặc block không hiện trên thiết bị đang xem.",
          "Nhãn Chưa lưu màu cam: block có chỉnh sửa trong trình sửa nhưng chưa bấm Lưu block.",
        ],
      },
      {
        title: "Đổi thứ tự block",
        steps: [
          "Giữ tay kéo (6 chấm) ở đầu dòng rồi kéo lên hoặc xuống, thả ở vị trí mới.",
          "Hoặc bấm mũi tên lên, xuống ở cột Thao tác để dịch từng vị trí.",
          "Thứ tự mới được lưu tự động. Số ở cột STT là vị trí của block trên trang chủ, từ trên xuống.",
        ],
      },
      {
        title: "Bảng chi tiết block đang chọn",
        intro: "Bấm vào một dòng trong danh sách để xem và chỉnh nhanh block đó ở bảng bên phải.",
        shot: {
          file: "list-detail",
          caption: "Bảng chi tiết bên phải màn danh sách",
          marks: [
            "Vị trí của block trên trang, mẫu block, tên và mã block.",
            "Ảnh thiết kế của block, bấm để phóng to.",
            "Hiển thị trên máy tính, điện thoại. Bỏ chọn thiết bị nào thì block ẩn trên thiết bị đó.",
            "Lịch hiển thị: Hiện từ, Đến. Để trống là luôn hiển thị; hết lịch, block tự ẩn.",
            "Mở trình sửa nội dung block.",
            "Nhân bản: tạo bản sao ngay dưới block này, bản sao đang ẩn.",
            "Xoá block: hệ thống hỏi lại trước khi xoá.",
          ],
        },
        tips: [
          "Xoá block là xoá luôn trên hệ thống, không hoàn tác được. Nếu chỉ muốn tạm ẩn, tắt công tắc Bật.",
        ],
      },
    ],
  },
  {
    key: "them-block",
    title: "Thêm block mới",
    summary:
      "Mỗi block được tạo từ một trong 13 mẫu. Mẫu quyết định bố cục và các trường cần nhập. Thêm xong, hệ thống mở thẳng trình sửa nội dung của block mới.",
    sections: [
      {
        title: "Các bước thêm block",
        steps: [
          "Ở màn danh sách, bấm Thêm block ở góc phải thanh công cụ.",
          "Chọn một mẫu trong lưới. Gõ vào ô tìm kiếm để lọc nhanh, ví dụ: sản phẩm, banner, video.",
          "Đặt tên block. Tên này hiện trong trang quản trị và là tiêu đề mặc định trên web.",
          "Chọn vị trí chèn: block mới nằm ngay sau block được chọn.",
          "Chọn trạng thái sau khi thêm rồi bấm Thêm và sửa nội dung.",
        ],
        shot: {
          file: "add-modal",
          caption: "Cửa sổ Thêm block trang chủ",
          marks: [
            "Tìm mẫu theo tên hoặc mô tả.",
            "Thẻ mẫu, bấm để chọn. Thẻ đang chọn có viền xanh; dòng Đang dùng cho biết block nào đang dùng mẫu này.",
            "Ảnh và mô tả của mẫu đã chọn để đối chiếu.",
            "Tên block.",
            "Vị trí chèn trên trang chủ.",
            "Trạng thái sau khi thêm: ẩn, hoặc hiển thị ngay.",
            "Tạo block và mở trình sửa nội dung.",
          ],
        },
        tips: [
          "Nên chọn Ẩn khi thêm, nhập đủ nội dung rồi mới bật block ở màn danh sách.",
          "Muốn có một block giống block đang có, dùng Nhân bản ở bảng chi tiết thay vì thêm mới.",
        ],
      },
    ],
  },
  {
    key: "sua-va-luu",
    title: "Sửa nội dung và lưu",
    summary:
      "Màn sửa block có 3 tab: Nội dung, Hiển thị và lịch, Dữ liệu lưu. Mọi thay đổi ở màn sửa chỉ ghi xuống hệ thống khi bấm Lưu block.",
    sections: [
      {
        title: "Đầu màn sửa",
        shot: {
          file: "edit-header",
          caption: "Đầu màn sửa block",
          marks: [
            "Quay về màn danh sách block.",
            "Mẫu, mã block và vị trí trên trang chủ. Nhãn cam Có thay đổi chưa lưu hiện khi còn chỉnh sửa chưa lưu.",
            "Mở hướng dẫn của mẫu đang sửa trong tab mới, phần đang sửa vẫn giữ nguyên.",
            "Huỷ thay đổi: rời màn sửa. Nếu còn thay đổi chưa lưu, hệ thống hỏi có lưu không.",
            "Lưu block: tải ảnh mới lên CDN rồi lưu toàn bộ nội dung.",
            "Các tab: Nội dung, Hiển thị và lịch, Dữ liệu lưu.",
          ],
        },
      },
      {
        title: "Lưu block và ảnh tải lên",
        steps: [
          "Ảnh chọn từ máy chỉ hiện để xem trước, chưa tải lên CDN.",
          "Bấm Lưu block: hệ thống tải tất cả ảnh mới lên CDN trong một lần, tải xong mới lưu nội dung block.",
          "Tải ảnh lỗi thì block chưa được lưu. Đọc thông báo lỗi, kiểm tra lại ảnh rồi bấm Lưu block lần nữa.",
        ],
        tips: [
          "Ảnh nhận PNG, JPG hoặc WebP. Ảnh làm icon nhận PNG, SVG hoặc WebP.",
          "Mỗi block lưu riêng. Sửa nhiều block thì bấm Lưu block ở từng block.",
        ],
      },
      {
        title: "Rời màn khi chưa lưu",
        intro:
          "Bấm Huỷ thay đổi, bấm Danh sách block hoặc chuyển sang menu khác khi còn thay đổi chưa lưu, hệ thống hỏi trước khi rời đi.",
        shot: {
          file: "edit-unsaved",
          caption: "Cửa sổ hỏi lưu khi rời màn sửa",
          marks: [
            "Ở lại màn sửa để làm tiếp.",
            "Không lưu: bỏ mọi thay đổi chưa lưu của block rồi rời màn.",
            "Lưu lại: lưu block, kể cả tải ảnh lên CDN, rồi rời màn. Lưu lỗi thì ở lại để sửa.",
          ],
        },
        tips: [
          "Tải lại trang hoặc đóng tab khi chưa lưu, trình duyệt sẽ hỏi xác nhận. Ảnh mới chọn mà chưa lưu sẽ mất.",
        ],
      },
      {
        title: "Tab Hiển thị và lịch",
        shot: {
          file: "edit-display",
          caption: "Tab Hiển thị và lịch",
          marks: [
            "Bật hoặc tắt block trên trang chủ.",
            "Thiết bị hiển thị: máy tính, điện thoại hoặc cả hai.",
            "Lịch hiển thị. Để trống là luôn hiển thị; hết lịch, block tự ẩn.",
            "Mã neo trên web, dùng cho link nhảy thẳng tới block. Mã này không sửa được.",
            "Ảnh thiết kế của block để đối chiếu, bấm để phóng to.",
          ],
        },
        tips: [
          "Ở tab này, thay đổi chỉ được lưu khi bấm Lưu block. Ở màn danh sách thì các mục này tự lưu ngay.",
        ],
      },
      {
        title: "Tab Dữ liệu lưu",
        intro:
          "Xem đúng dữ liệu sẽ gửi lên hệ thống. Dùng khi cần kiểm tra lại, hoặc gửi cho bộ phận kỹ thuật khi có lỗi.",
        shot: {
          file: "edit-data",
          caption: "Tab Dữ liệu lưu",
          marks: [
            "Các cột riêng của block: tên, mã, mẫu, thiết bị, lịch, thứ tự.",
            "Nội dung chi tiết của block dạng JSON.",
            "Chép JSON để gửi hoặc lưu lại.",
          ],
        },
      },
    ],
  },
  {
    key: "thao-tac-chung",
    title: "Ảnh, icon và danh sách",
    summary:
      "Ô ảnh, ô chọn icon và danh sách kéo thả dùng chung cho mọi mẫu block. Nắm phần này trước thì các mẫu bên dưới đều làm giống nhau.",
    sections: [
      {
        title: "Ô ảnh lớn",
        intro: "Dùng cho ảnh banner, ảnh thẻ, ảnh nền.",
        steps: [
          "Bấm thẳng vào ảnh đang hiện, hoặc bấm Đổi ảnh, rồi chọn ảnh từ máy.",
          "Ô trống có chữ Kéo ảnh vào đây hoặc chọn từ Media: bấm vào ô để chọn ảnh.",
          "Nhãn Ảnh mẫu từ thiết kế nghĩa là block chưa có ảnh thật. Ảnh mẫu chỉ để xem, không được lưu.",
          "Bấm Gỡ ảnh để bỏ ảnh đang dùng.",
        ],
        shot: {
          file: "kit-image",
          caption: "Ô ảnh lớn ở mẫu Lưới danh mục",
          marks: [
            "Ảnh đang dùng: bấm thẳng vào ảnh để chọn ảnh khác.",
            "Rê chuột lên ảnh sẽ thấy chú thích Bấm để đổi ảnh.",
            "Đổi ảnh, Gỡ ảnh.",
          ],
        },
      },
      {
        title: "Ô ảnh nhỏ trong danh sách",
        steps: [
          "Bấm vào ô ảnh nhỏ ở đầu dòng để tải ảnh khác.",
          "Rê chuột lên ô ảnh để xem ảnh đang lấy từ đâu: ảnh riêng, ảnh của danh mục, ảnh của banner, ảnh mẫu từ thiết kế.",
          "Ô có chấm xanh ở góc là đang dùng ảnh riêng. Bấm vào để chọn Tải ảnh khác, hoặc quay về ảnh mặc định.",
          "Ô trống có biểu tượng ảnh: chưa có ảnh, hoặc link ảnh đã hỏng (rê chuột sẽ thấy Ảnh không tải được). Bấm để tải ảnh khác.",
        ],
        shot: {
          file: "kit-thumb",
          caption: "Ô ảnh nhỏ ở mẫu Lưới danh mục",
          marks: [
            "Ô ảnh có chấm xanh ở góc: đang dùng ảnh riêng. Bấm vào ô để mở tuỳ chọn.",
            "Tải ảnh khác từ máy.",
            "Bỏ ảnh riêng, dùng lại ảnh của danh mục.",
          ],
        },
      },
      {
        title: "Chọn icon",
        steps: [
          "Bấm vào ô icon để mở bảng chọn.",
          "Tab Chọn icon: gõ để tìm, gõ không dấu cũng được (ví dụ: giao hang), rồi bấm vào icon. Nhấn Enter để chọn icon đầu tiên khớp.",
          "Tab Tải ảnh lên, hoặc nút tải lên ngay trên ô icon: dùng ảnh riêng làm icon. Nên dùng PNG, SVG hoặc WebP nền trong suốt, ảnh vuông từ 64×64 px.",
          "Bấm Bỏ icon ở cuối bảng để xoá icon.",
        ],
        shot: {
          file: "kit-icon",
          caption: "Bảng chọn icon ở mẫu Lưới danh mục",
          marks: [
            "Ô icon: bấm để mở bảng chọn.",
            "Nút tải ảnh làm icon.",
            "Chọn icon có sẵn, hoặc tải ảnh lên.",
            "Tìm icon theo tên.",
            "Bảng icon: bấm để chọn.",
            "Bỏ icon đang chọn.",
          ],
        },
      },
      {
        title: "Danh sách kéo thả",
        steps: [
          "Kéo tay kéo (6 chấm) ở đầu dòng để đổi thứ tự.",
          "Bấm thùng rác ở cuối dòng để xoá mục.",
          "Bấm nút Thêm dưới danh sách để thêm mục mới. Số bên cạnh cho biết đã dùng bao nhiêu trên tối đa; đủ số thì nút Thêm ẩn đi.",
          "Danh sách dạng thẻ (ví dụ thẻ quảng bá, thẻ nhóm khách hàng): bấm vào tiêu đề thẻ để mở hoặc đóng phần nhập liệu.",
        ],
        shot: {
          file: "kit-list",
          caption: "Danh sách kéo thả ở mẫu Logo và giải pháp",
          marks: [
            "Tay kéo đổi thứ tự.",
            "Xoá mục.",
            "Thêm mục và số mục đã dùng trên tối đa.",
          ],
        },
      },
    ],
  },
];

export const GUIDE_TEMPLATES: Record<HomeBlockTemplate, GuideTopic> = {
  hero: {
    key: "hero",
    title: "Hero",
    summary:
      "Khối đầu trang chủ gồm slider banner lớn, 3 banner phụ bên phải và dải cam kết bên dưới. Menu đầu trang và menu danh mục bên trái là phần cố định của web, không sửa ở block này.",
    sections: [
      {
        title: "Slider chính",
        steps: [
          "Bấm Thêm từ Danh sách banner, gõ tên banner để tìm rồi bấm chọn. Mỗi slide là một banner đã có trong Danh sách banner.",
          "Kéo tay kéo để đổi thứ tự slide; slide trên cùng hiện trước.",
          "Bấm vào ảnh slide để dùng ảnh riêng cho slide đó. Không chọn thì slider lấy ảnh của banner.",
          "Tắt công tắc Bật để tạm ẩn slide mà không xoá. Bấm thùng rác để bỏ slide khỏi slider.",
        ],
        shot: {
          file: "hero-slider",
          caption: "Phần Slider chính",
          marks: [
            "Thêm slide từ Danh sách banner.",
            "Tay kéo đổi thứ tự slide.",
            "Ảnh slide: bấm để dùng ảnh riêng.",
            "Tên banner trong Danh sách banner.",
            "Lịch hiển thị, lấy theo banner.",
            "Bật hoặc tắt slide.",
            "Đổi sang banner khác.",
            "Bỏ slide khỏi slider.",
          ],
        },
        tips: [
          "Link và lịch hiển thị của slide sửa ở màn Danh sách banner.",
          "Slider tự chuyển sau 5 giây và dừng khi rê chuột.",
        ],
      },
      {
        title: "3 banner phụ bên phải",
        steps: [
          "Mỗi ô có ảnh máy tính, ảnh điện thoại (tuỳ chọn), link và mô tả ảnh.",
          "Bấm vào ảnh để đổi ảnh. Không có ảnh điện thoại thì web dùng ảnh máy tính.",
          "Kéo tay kéo ở đầu thẻ để đổi vị trí các ô.",
        ],
        shot: {
          file: "hero-side",
          caption: "Phần 3 banner phụ bên phải",
          marks: [
            "Tay kéo đổi vị trí ô.",
            "Ảnh máy tính.",
            "Ảnh điện thoại, tuỳ chọn.",
            "Link khi bấm vào banner.",
            "Mô tả ảnh, dùng cho người đọc màn hình và SEO.",
          ],
        },
      },
      {
        title: "Dải cam kết",
        steps: [
          "Mỗi cam kết gồm icon, tiêu đề và mô tả ngắn, tối đa 6 cam kết.",
          "Nhãn tổng đài và số tổng đài hiện ở cuối dải.",
        ],
        shot: {
          file: "hero-usp",
          caption: "Phần Dải cam kết",
          marks: [
            "Icon cam kết.",
            "Tiêu đề cam kết.",
            "Mô tả ngắn.",
            "Số cam kết đã dùng, tối đa 6. Đủ 6 thì nút Thêm cam kết ẩn đi.",
            "Nhãn tổng đài và số tổng đài.",
          ],
        },
        tips: ["Mục lục ở cột phải màn sửa giúp nhảy nhanh tới từng phần của block."],
      },
    ],
  },
  catgrid: {
    key: "catgrid",
    title: "Lưới danh mục",
    summary:
      "Khối gồm đầu block (tiêu đề, mô tả, nút Xem tất cả), một ô quảng bá và lưới ô danh mục. Ô danh mục lấy theo Danh mục sản phẩm, số sản phẩm tự đếm.",
    sections: [
      {
        title: "Đầu block",
        intro:
          "Phần tô màu nối ngay sau tiêu đề và hiện màu đỏ. Ví dụ tiêu đề \"Công nghệ dành cho\" cùng phần tô màu \"mọi nhu cầu\" hiện thành \"Công nghệ dành cho mọi nhu cầu\".",
        shot: {
          file: "catgrid-head",
          caption: "Phần Đầu block",
          marks: [
            "Nhãn nhỏ phía trên tiêu đề.",
            "Tiêu đề block.",
            "Phần tô màu trong tiêu đề.",
            "Mô tả dưới tiêu đề.",
            "Nhãn và link nút Xem tất cả.",
          ],
        },
      },
      {
        title: "Ô quảng bá",
        shot: {
          file: "catgrid-promo",
          caption: "Phần Ô quảng bá",
          marks: [
            "Ảnh ô quảng bá.",
            "Tiêu đề.",
            "Nhãn nút và link.",
            "Gạch đầu dòng, tối đa 3 dòng.",
          ],
        },
      },
      {
        title: "Ô danh mục",
        steps: [
          "Mỗi dòng là một ô trong lưới, tối đa 14 ô. Kéo tay kéo để đổi thứ tự.",
          "Chọn danh mục: ảnh và tên ô lấy theo danh mục. Bấm ảnh nhỏ để dùng ảnh riêng.",
          "Liên kết con: chọn tối đa 4 danh mục hiện dưới tên ô.",
          "Bấm nút Tuỳ chỉnh hàng tiêu đề (biểu tượng thanh trượt) ở cuối dòng để đổi icon, tên hiển thị, dòng số sản phẩm và link nút mũi tên.",
        ],
        shot: {
          file: "catgrid-tiles",
          caption: "Danh sách ô danh mục",
          marks: [
            "Ảnh ô, lấy từ danh mục. Bấm để dùng ảnh riêng.",
            "Danh mục của ô.",
            "Liên kết con, tối đa 4 danh mục.",
            "Tuỳ chỉnh hàng tiêu đề của ô.",
            "Số ô đã dùng, tối đa 14. Đủ 14 thì nút Thêm ô danh mục ẩn đi.",
          ],
        },
      },
      {
        title: "Tuỳ chỉnh hàng tiêu đề của ô",
        shot: {
          file: "catgrid-tile-detail",
          caption: "Phần tuỳ chỉnh mở dưới một ô danh mục",
          marks: [
            "Icon của ô.",
            "Tên hiển thị. Để trống thì dùng tên danh mục.",
            "Số sản phẩm: Tự đếm theo danh mục, Nhập tay, hoặc Ẩn.",
            "Link nút mũi tên. Để trống thì mở trang của danh mục.",
          ],
        },
        tips: [
          "Tự đếm làm tròn xuống, ví dụ danh mục có 3.247 sản phẩm hiện \"3.200+ sản phẩm\".",
        ],
      },
    ],
  },
  persona: {
    key: "persona",
    title: "Thẻ nhu cầu",
    summary:
      "Khối thẻ ảnh theo nhóm khách hàng (sinh viên, game thủ, dân văn phòng...). Mỗi thẻ có gạch đầu dòng, các gợi ý dẫn tới danh mục sản phẩm và nút khám phá.",
    sections: [
      {
        title: "Đầu block",
        shot: {
          file: "persona-head",
          caption: "Phần Đầu block",
          marks: [
            "Tiêu đề block.",
            "Phần tô màu, nối sau tiêu đề.",
            "Mô tả.",
            "Cam kết nhỏ ở góc phải đầu block, tối đa 3.",
          ],
        },
      },
      {
        title: "Thẻ nhóm khách hàng",
        steps: [
          "Bấm vào tiêu đề thẻ để mở phần nhập liệu. Kéo tay kéo để đổi thứ tự thẻ, tối đa 8 thẻ.",
          "Bấm ảnh nhỏ ở đầu thẻ, hoặc ảnh lớn bên trong, để đổi ảnh thẻ.",
          "Mỗi thẻ có tối đa 3 gợi ý. Gợi ý gồm icon, danh mục sản phẩm và tên hiển thị.",
        ],
        shot: {
          file: "persona-card",
          caption: "Một thẻ nhóm khách hàng đang mở",
          marks: [
            "Ảnh nhỏ của thẻ: bấm để đổi ảnh.",
            "Tiêu đề thẻ: bấm để mở hoặc đóng.",
            "Ảnh thẻ.",
            "Nhóm khách hàng và icon.",
            "Gạch đầu dòng.",
            "Nhãn nút và link.",
            "Gợi ý: icon, danh mục, tên hiển thị.",
          ],
        },
      },
    ],
  },
  product: {
    key: "product",
    title: "Ngành hàng sản phẩm",
    summary:
      "Khối ngành hàng (Laptop, PC, Màn hình, Văn phòng...) gồm banner lớn, dải logo thương hiệu, dải thông báo, ô danh mục nhanh và các tab sản phẩm.",
    sections: [
      {
        title: "Thông tin chung và banner lớn",
        shot: {
          file: "product-head",
          caption: "Phần Thông tin chung và Banner lớn",
          marks: [
            "Tiêu đề block.",
            "Nhãn và link Xem tất cả.",
            "Ảnh banner trên máy tính.",
            "Ảnh banner trên điện thoại.",
            "Link khi bấm banner và mô tả ảnh.",
          ],
        },
      },
      {
        title: "Thương hiệu",
        steps: [
          "Bật Hiện dải thương hiệu nếu block cần dải logo hãng.",
          "Tìm và chọn thương hiệu từ Danh sách thương hiệu; logo lấy theo thương hiệu.",
          "Kéo để sắp xếp thứ tự logo.",
        ],
        shot: {
          file: "product-brands",
          caption: "Phần Thương hiệu",
          marks: [
            "Bật, tắt dải thương hiệu.",
            "Chọn và sắp xếp thương hiệu.",
            "Nhãn và link nút ở cuối dải logo. Để trống nhãn thì ẩn nút.",
          ],
        },
      },
      {
        title: "Dải thông báo",
        shot: {
          file: "product-notice",
          caption: "Phần Dải thông báo",
          marks: [
            "Bật, tắt dải thông báo.",
            "Tiêu đề.",
            "Mô tả.",
            "Nhãn nút và link.",
          ],
        },
      },
      {
        title: "Ô danh mục nhanh",
        shot: {
          file: "product-quick",
          caption: "Phần Ô danh mục nhanh",
          marks: [
            "Ảnh ô, lấy từ danh mục. Bấm để dùng ảnh riêng.",
            "Tên ô.",
            "Danh mục liên kết.",
            "Thêm ô, tối đa 6 ô.",
          ],
        },
      },
      {
        title: "Tab sản phẩm",
        steps: [
          "Cột trái là danh sách tab: bấm tab để sửa, kéo để đổi thứ tự. Bấm Thêm tab ở góc phải để thêm tab.",
          "Đặt tên tab. Bật Mở tab này khi tải trang cho tab hiện sẵn.",
          "Lấy sản phẩm: Chọn từng sản phẩm (gõ ít nhất 2 ký tự tên hoặc mã sản phẩm), hoặc Chọn từ danh mục để trang chủ tự lấy sản phẩm thuộc danh mục.",
          "Chọn các nhãn hiện trên thẻ sản phẩm.",
        ],
        shot: {
          file: "product-tabs",
          caption: "Phần Tab sản phẩm",
          marks: [
            "Danh sách tab: bấm để sửa, kéo để đổi thứ tự.",
            "Thêm tab.",
            "Tên tab.",
            "Tab mở sẵn khi tải trang.",
            "Cách lấy sản phẩm: chọn từng sản phẩm, hoặc chọn từ danh mục.",
            "Chọn sản phẩm, hoặc chọn danh mục.",
            "Nhãn trên thẻ sản phẩm.",
            "Xoá tab đang sửa.",
          ],
        },
        tips: [
          "Chọn danh mục sẽ bỏ các mã sản phẩm đang có của tab.",
          "Khung Kiểm tra trước khi lưu ở cột phải liệt kê các điểm cần xem lại trước khi bấm Lưu block.",
        ],
      },
    ],
  },
  banner: {
    key: "banner",
    title: "Banner",
    summary:
      "Một banner lớn có nút hành động, có ảnh riêng cho điện thoại. Lịch hiển thị riêng của banner đặt ở tab Hiển thị và lịch.",
    sections: [
      {
        title: "Ảnh banner",
        shot: {
          file: "banner-images",
          caption: "Phần Ảnh banner",
          marks: ["Ảnh trên máy tính.", "Ảnh trên điện thoại."],
        },
        tips: [
          "Không có ảnh điện thoại, web thu nhỏ ảnh máy tính nên chữ trên banner sẽ rất nhỏ. Nên tải ảnh riêng cho điện thoại.",
        ],
      },
      {
        title: "Liên kết và nút",
        shot: {
          file: "banner-link",
          caption: "Phần Liên kết và nút",
          marks: [
            "Link khi bấm vào banner.",
            "Nhãn nút nằm trên ảnh. Để trống nếu chữ nút đã có trong ảnh.",
            "Mô tả ảnh, dùng cho người đọc màn hình và SEO.",
          ],
        },
      },
    ],
  },
  tiles: {
    key: "tiles",
    title: "Lưới ô",
    summary:
      "Lưới ô ảnh nhiều kích thước dẫn tới danh mục hoặc trang (Linh kiện PC, Gaming Gear, Console, Mạng, Nhà thông minh). Bố cục lưới cố định theo thiết kế của từng block.",
    sections: [
      {
        title: "Thông tin chung",
        shot: {
          file: "tiles-head",
          caption: "Phần Thông tin chung",
          marks: [
            "Tiêu đề block và phần tô màu.",
            "Dòng phụ dưới tiêu đề.",
            "Nhãn và link Xem tất cả. Để trống nhãn thì ẩn nút.",
          ],
        },
      },
      {
        title: "Các ô",
        steps: [
          "Sơ đồ cho biết vị trí từng ô trên web; số trong sơ đồ là thứ tự ô ở danh sách bên dưới.",
          "Kéo tay kéo để đổi chỗ ô.",
          "Bấm vào dòng của ô để mở phần nhập liệu.",
        ],
        shot: {
          file: "tiles-cells",
          caption: "Phần Các ô",
          marks: [
            "Sơ đồ bố cục lưới.",
            "Số ô đã có nội dung.",
            "Tay kéo đổi chỗ ô.",
            "Ảnh ô: bấm để đổi ảnh.",
            "Dòng của ô: bấm để mở hoặc đóng.",
          ],
        },
      },
      {
        title: "Nội dung một ô",
        shot: {
          file: "tiles-cell-detail",
          caption: "Ô lớn đang mở",
          marks: [
            "Danh mục của ô.",
            "Link. Để trống thì mở trang của danh mục.",
            "Ảnh máy tính và ảnh điện thoại của ô.",
            "Nhãn nhỏ và tiêu đề: tiêu đề, phần tô màu, phần cuối tiêu đề.",
            "Mô tả, tối đa 2 dòng trên web, xuống dòng bằng Enter.",
            "Điểm nổi bật, tối đa 4.",
            "Nhãn nút. Để trống thì chỉ hiện nút mũi tên.",
          ],
        },
        tips: [
          "Ô nhỏ chỉ có danh mục, link, ảnh, tiêu đề và mô tả. Để trống tiêu đề thì dùng tên danh mục.",
          "Bố cục Banner trên và 6 ô (Nhà thông minh) có thêm Khối danh mục phụ bên phải: tiêu đề khối, nhãn link và tối đa 6 danh mục có ảnh.",
        ],
      },
    ],
  },
  business: {
    key: "business",
    title: "Logo và giải pháp",
    summary:
      "Khối giới thiệu khách hàng doanh nghiệp: đầu block có 2 nút, lĩnh vực khách hàng, logo khách hàng tiêu biểu, quyền lợi và các nhóm dịch vụ chính.",
    sections: [
      {
        title: "Đầu block",
        shot: {
          file: "business-head",
          caption: "Phần Đầu block",
          marks: [
            "Nhãn nhỏ và dòng phụ cạnh nhãn.",
            "Tiêu đề và phần tô màu.",
            "Mô tả.",
            "Nút chính và link.",
            "Nút phụ và link.",
            "Ảnh nền bên phải.",
          ],
        },
      },
      {
        title: "Lĩnh vực khách hàng và quyền lợi",
        intro:
          "Hai danh sách làm giống nhau: mỗi mục có icon và nhãn, tối đa 6 mục. Ảnh dưới là phần Lĩnh vực khách hàng.",
        shot: {
          file: "business-fields",
          caption: "Phần Lĩnh vực khách hàng",
          marks: ["Icon.", "Nhãn.", "Thêm lĩnh vực, tối đa 6."],
        },
      },
      {
        title: "Logo khách hàng tiêu biểu",
        shot: {
          file: "business-logos",
          caption: "Phần Logo khách hàng tiêu biểu",
          marks: ["Logo: bấm để tải ảnh logo.", "Tên khách hàng.", "Link, tuỳ chọn."],
        },
        tips: ["Tối đa 20 logo. Kéo tay kéo ở đầu dòng để đổi thứ tự logo."],
      },
      {
        title: "Các nhóm dịch vụ chính",
        shot: {
          file: "business-services",
          caption: "Phần Các nhóm dịch vụ chính",
          marks: ["Ảnh.", "Tiêu đề.", "Mô tả.", "Link.", "Số nhóm đã dùng, tối đa 8."],
        },
      },
    ],
  },
  service: {
    key: "service",
    title: "Dịch vụ",
    summary:
      "Khối dịch vụ HACOM: đầu block có khẩu hiệu, cam kết và ảnh nền; danh mục dịch vụ; quy trình theo bước kèm nút đăng ký.",
    sections: [
      {
        title: "Đầu block",
        shot: {
          file: "service-head",
          caption: "Phần Đầu block",
          marks: [
            "Tiêu đề và phần tô màu.",
            "Khẩu hiệu và dòng phụ.",
            "Cam kết dưới tiêu đề, tối đa 4.",
            "Điểm nổi bật bên phải, tối đa 4.",
            "Ảnh nền.",
          ],
        },
      },
      {
        title: "Danh mục dịch vụ",
        shot: {
          file: "service-list",
          caption: "Phần Danh mục dịch vụ",
          marks: ["Ảnh dịch vụ: bấm để đổi ảnh.", "Nhãn.", "Link."],
        },
        tips: ["Tối đa 16 dịch vụ. Đủ 16 thì nút Thêm dịch vụ ẩn đi, xoá bớt một mục để thêm mục mới."],
      },
      {
        title: "Quy trình dịch vụ",
        shot: {
          file: "service-steps",
          caption: "Phần Quy trình dịch vụ",
          marks: [
            "Nhãn và mô tả của quy trình.",
            "Các bước, tối đa 5.",
            "Nhãn nút và link trang đăng ký dịch vụ.",
          ],
        },
      },
    ],
  },
  numbers: {
    key: "numbers",
    title: "Con số",
    summary: "Các ô số liệu nổi bật của HACOM. Mỗi ô có ảnh, con số, nhãn, mô tả ngắn và link.",
    sections: [
      {
        title: "Đầu block",
        shot: {
          file: "numbers-head",
          caption: "Phần Đầu block",
          marks: ["Tiêu đề.", "Phụ đề.", "Mô tả."],
        },
      },
      {
        title: "Ô số liệu",
        shot: {
          file: "numbers-list",
          caption: "Phần Ô số liệu",
          marks: ["Ảnh.", "Con số.", "Nhãn.", "Mô tả.", "Link.", "Số ô đã dùng, tối đa 8."],
        },
        tips: [
          "Chỉ nhập số liệu đã được xác nhận. Số trong dữ liệu mẫu lấy từ bản thiết kế, cần kiểm tra lại trước khi lưu.",
        ],
      },
    ],
  },
  news: {
    key: "news",
    title: "Tin tức",
    summary:
      "Khối tin tức gồm tab chuyên mục, bài lớn bên trái, lưới bài ở giữa, cột bài xếp hạng bên phải, banner đầu block và dải cam kết cuối block.",
    sections: [
      {
        title: "Đầu block",
        shot: {
          file: "news-head",
          caption: "Phần Đầu block",
          marks: [
            "Nhãn nhỏ.",
            "Tiêu đề và phần tô màu.",
            "Mô tả.",
            "Nhãn và link Xem tất cả.",
          ],
        },
      },
      {
        title: "Tab chuyên mục",
        shot: {
          file: "news-tabs",
          caption: "Phần Tab chuyên mục",
          marks: [
            "Tên tab.",
            "Chuyên mục bài viết, chọn từ Danh mục bài viết.",
            "Thêm tab và số tab đã dùng, tối đa 8.",
          ],
        },
      },
      {
        title: "Bài viết",
        steps: [
          "Bài lớn bên trái: chọn Mới nhất của tab để tự lấy bài mới nhất, hoặc Chọn tay để chọn một bài cố định.",
          "Đặt số bài ở lưới giữa, tiêu đề, cách xếp và số bài của cột phải.",
        ],
        shot: {
          file: "news-articles",
          caption: "Phần Bài viết",
          marks: [
            "Bài lớn bên trái: mới nhất của tab, hoặc chọn tay.",
            "Số bài ở lưới giữa.",
            "Tiêu đề cột phải.",
            "Cột phải xếp theo.",
            "Số bài cột phải.",
          ],
        },
      },
      {
        title: "Banner bên phải đầu block",
        shot: {
          file: "news-banner",
          caption: "Phần Banner bên phải đầu block",
          marks: ["Ảnh banner.", "Tiêu đề.", "Nhãn nút và link.", "Gạch đầu dòng, tối đa 3."],
        },
      },
      {
        title: "Dải cam kết cuối block",
        shot: {
          file: "news-usp",
          caption: "Phần Dải cam kết cuối block",
          marks: [
            "Cam kết: icon, tiêu đề, mô tả.",
            "Số cam kết đã dùng, tối đa 4.",
            "Nút cuối block và link.",
          ],
        },
      },
    ],
  },
  video: {
    key: "video",
    title: "Video",
    summary:
      "Danh sách video review trượt ngang, lấy từ một playlist YouTube hoặc từ danh sách link video nhập tay.",
    sections: [
      {
        title: "Đầu block",
        shot: {
          file: "video-head",
          caption: "Phần Đầu block",
          marks: ["Tiêu đề.", "Phần tô màu trong tiêu đề.", "Nhãn và link Xem tất cả."],
        },
      },
      {
        title: "Nguồn video",
        steps: [
          "Playlist YouTube: dán link playlist, chọn số video hiển thị từ 3 đến 12.",
          "Danh sách link: thêm từng video gồm ảnh bìa, tiêu đề và link YouTube, tối đa 12 video.",
        ],
        shot: {
          file: "video-source",
          caption: "Phần Nguồn video, chế độ Danh sách link",
          marks: [
            "Lấy video từ playlist YouTube, hoặc danh sách link.",
            "Ảnh bìa video: bấm để tải ảnh khác.",
            "Tiêu đề video.",
            "Link YouTube của video.",
            "Thêm video và số video đã dùng, tối đa 12.",
          ],
        },
      },
      {
        title: "Hiển thị trên thẻ video",
        shot: {
          file: "video-display",
          caption: "Phần Hiển thị trên thẻ video",
          marks: ["Chọn thông tin hiện trên thẻ: lượt xem, thời lượng, tên kênh, ngày đăng."],
        },
        tips: ["Số liệu lấy trực tiếp từ YouTube, không nhập tay."],
      },
    ],
  },
  gallery: {
    key: "gallery",
    title: "Thư viện ảnh",
    summary:
      "Ảnh khách hàng mua hàng tại HACOM kèm lời nhận xét và nơi mua, cùng các chỉ số và lời cảm ơn.",
    sections: [
      {
        title: "Đầu block",
        shot: {
          file: "gallery-head",
          caption: "Phần Đầu block",
          marks: [
            "Nhãn nhỏ.",
            "Tiêu đề và phần tô màu.",
            "Lời cảm ơn viết tay.",
            "Mô tả.",
            "Nhãn nút và link.",
            "Dòng chữ cuối block.",
          ],
        },
      },
      {
        title: "Chỉ số",
        shot: {
          file: "gallery-stats",
          caption: "Phần Chỉ số",
          marks: ["Icon.", "Con số.", "Mô tả.", "Số chỉ số đã dùng, tối đa 4."],
        },
        tips: ["Chỉ nhập số liệu đã được xác nhận."],
      },
      {
        title: "Ảnh khách hàng",
        steps: [
          "Bấm Chọn từ Danh sách ảnh khách hàng, tìm theo chi nhánh hoặc mã sản phẩm rồi bấm chọn.",
          "Nhập lời nhận xét và nơi mua cho từng ảnh.",
          "Ảnh đầu tiên hiện khổ lớn bên trái; các ảnh sau chia 2 hàng, trượt ngang khi nhiều hơn 9 ảnh.",
          "Bấm Thêm ảnh tải lên để dùng ảnh từ máy thay vì chọn từ danh sách.",
        ],
        shot: {
          file: "gallery-photos",
          caption: "Phần Ảnh khách hàng",
          marks: [
            "Chọn ảnh từ Danh sách ảnh khách hàng.",
            "Ảnh.",
            "Lời nhận xét.",
            "Nơi mua.",
            "Nguồn ảnh: từ danh sách, hoặc tải lên.",
            "Thêm ảnh tải lên từ máy, và số ảnh đã dùng trên tối đa 20.",
          ],
        },
      },
    ],
  },
  promo: {
    key: "promo",
    title: "Thẻ quảng bá",
    summary:
      "Các thẻ dọc dẫn tới kênh bán hoặc tính năng (AI tư vấn, Shopee, TikTok, PC Builder, Flash Sale). Tối đa 5 thẻ.",
    sections: [
      {
        title: "Thẻ quảng bá",
        steps: [
          "Bấm vào tiêu đề thẻ để mở phần nhập liệu. Kéo tay kéo để đổi thứ tự thẻ.",
          "Chọn màu chủ đạo của thẻ, nhập nhãn, chữ góc phải, tiêu đề và mô tả.",
          "Nhập nhãn nút và link; thêm tối đa 3 điểm nổi bật ở cuối thẻ.",
        ],
        shot: {
          file: "promo-card",
          caption: "Một thẻ quảng bá đang mở",
          marks: [
            "Ảnh nhỏ của thẻ: bấm để đổi ảnh.",
            "Tiêu đề thẻ: bấm để mở hoặc đóng.",
            "Ảnh minh hoạ.",
            "Màu chủ đạo.",
            "Nhãn và chữ góc phải.",
            "Tiêu đề và mô tả.",
            "Nhãn nút và link.",
            "Điểm nổi bật cuối thẻ, tối đa 3.",
            "Số thẻ đã dùng, tối đa 5.",
          ],
        },
      },
    ],
  },
};
